import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { previewAPI } from '../services/api';
import { getPhotoUrl } from '../services/upload';
import DisplayName from './DisplayName';
import DisplayRate from './DisplayRate';
import './BookingModal.css';

const BookingModal = ({ ambassador, onClose, onSubmit }) => {
  const { demoMode, user } = useAuth();

  // Pre-populate with brand-specific defaults for preview users
  const getDefaultEventDate = () => {
    const twoWeeksOut = new Date();
    twoWeeksOut.setDate(twoWeeksOut.getDate() + 14);
    return twoWeeksOut.toISOString().split('T')[0];
  };

  const isPreview = user?.isPreview;

  const [formData, setFormData] = useState({
    eventName: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    eventLocation: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [generatingAI, setGeneratingAI] = useState(false);

  // Auto-generate event details for preview brands on mount
  useEffect(() => {
    if (isPreview) {
      generateEventDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview]);

  const generateEventDetails = async () => {
    if (!isPreview) return;

    setGeneratingAI(true);

    try {
      const response = await previewAPI.generateEventDetails();
      const eventDetails = response.data;

      setFormData({
        eventName: eventDetails.eventName || '',
        eventDate: eventDetails.eventDate || '',
        startTime: eventDetails.startTime || '',
        endTime: eventDetails.endTime || '',
        eventLocation: eventDetails.eventLocation || '',
        notes: eventDetails.notes || '',
      });
    } catch (error) {
      console.error('Failed to generate event details:', error);
      // Fall back to default values
      setFormData({
        eventName: `${user?.company_name || 'Brand'} Event`,
        eventDate: getDefaultEventDate(),
        startTime: '10:00',
        endTime: '14:00',
        eventLocation: user?.location || '',
        notes: '',
      });
    } finally {
      setGeneratingAI(false);
    }
  };

  // Parse date string as local date (not UTC) to prevent timezone shifting
  const parseLocalDate = (dateString) => {
    if (!dateString) {
      console.error('parseLocalDate: No date string provided');
      return new Date();
    }

    // Handle both YYYY-MM-DD and full timestamp formats
    const dateOnly = dateString.split('T')[0]; // Get just the date part if it's a timestamp
    const [year, month, day] = dateOnly.split('-').map(Number);

    // Validate the parsed values
    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
      console.error('parseLocalDate: Invalid date string:', dateString);
      return new Date();
    }

    return new Date(year, month - 1, day);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Event Name validation
    if (!formData.eventName.trim()) {
      newErrors.eventName = 'Event name is required';
    }

    // Event Date validation
    if (!formData.eventDate) {
      newErrors.eventDate = 'Event date is required';
    } else {
      const selectedDate = parseLocalDate(formData.eventDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.eventDate = 'Event date cannot be in the past';
      } else if (selectedDate.getFullYear() < 2000 || selectedDate.getFullYear() > 2100) {
        newErrors.eventDate = 'Please enter a valid event date';
      }
    }

    // Start Time validation
    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    // End Time validation
    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    } else if (formData.startTime && formData.endTime <= formData.startTime) {
      newErrors.endTime = 'End time must be after start time';
    }

    // Event Location validation
    if (!formData.eventLocation.trim()) {
      newErrors.eventLocation = 'Event location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit({
        ...formData,
        ambassadorId: ambassador.id,
        ambassadorName: ambassador.name,
        hourlyRate: ambassador.hourly_rate,
        duration: duration,
        estimatedCost: estimatedCost,
      });
    }
  };

  const calculateDuration = () => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}`);
      const end = new Date(`2000-01-01T${formData.endTime}`);
      const diffMs = end - start;
      const hours = diffMs / (1000 * 60 * 60);
      return hours > 0 ? hours : 0;
    }
    return 0;
  };

  const duration = calculateDuration();
  const estimatedCost = duration > 0 ? duration * ambassador.hourly_rate : 0;

  return (
    <div className="booking-modal" onClick={onClose}>
      <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="booking-modal-header">
          <h1 className="booking-modal-title">
            {generatingAI ? 'Generating Event Details...' : 'Book Ambassador'}
          </h1>
          <div className="booking-modal-header-actions">
            {isPreview && (
              <button
                type="button"
                className="regenerate-button"
                onClick={generateEventDetails}
                disabled={generatingAI}
                title="Regenerate event details with AI"
              >
                {generatingAI ? '✨ Generating...' : '✨ Regenerate'}
              </button>
            )}
            <button className="booking-modal-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {/* Ambassador Info */}
        <div className="booking-ambassador-info">
          {ambassador.profile_photo && (
            <img
              src={getPhotoUrl(ambassador.profile_photo)}
              alt={ambassador.name}
              className="booking-ambassador-photo"
            />
          )}
          <div className="booking-ambassador-details">
            <h2><DisplayName user={ambassador} demoMode={demoMode} /></h2>
            <p className="booking-rate">
              <DisplayRate user={ambassador} rate={ambassador.hourly_rate} demoMode={demoMode} suffix="/hour" />
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="booking-form">
          {/* Event Name */}
          <div className="form-group">
            <label htmlFor="eventName">Event Name *</label>
            <input
              type="text"
              id="eventName"
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              placeholder="e.g., Summer Product Launch"
              className={errors.eventName ? 'error' : ''}
            />
            {errors.eventName && <span className="error-message">{errors.eventName}</span>}
          </div>

          {/* Event Date */}
          <div className="form-group">
            <label htmlFor="eventDate">Event Date *</label>
            <input
              type="date"
              id="eventDate"
              name="eventDate"
              value={formData.eventDate}
              onChange={handleChange}
              className={errors.eventDate ? 'error' : ''}
              min={new Date().toISOString().split('T')[0]}
              max="2100-12-31"
            />
            {errors.eventDate && <span className="error-message">{errors.eventDate}</span>}
          </div>

          {/* Time Fields */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startTime">Start Time *</label>
              <input
                type="time"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                className={errors.startTime ? 'error' : ''}
              />
              {errors.startTime && <span className="error-message">{errors.startTime}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="endTime">End Time *</label>
              <input
                type="time"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                className={errors.endTime ? 'error' : ''}
              />
              {errors.endTime && <span className="error-message">{errors.endTime}</span>}
            </div>
          </div>
          <p className="timezone-note">🕐 All times in Central Time (Chicago)</p>

          {/* Event Location */}
          <div className="form-group">
            <label htmlFor="eventLocation">Event Location *</label>
            <input
              type="text"
              id="eventLocation"
              name="eventLocation"
              value={formData.eventLocation}
              onChange={handleChange}
              placeholder="Address or venue name"
              className={errors.eventLocation ? 'error' : ''}
            />
            {errors.eventLocation && <span className="error-message">{errors.eventLocation}</span>}
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Share special instructions on what to do when you get there, what to wear, etc."
              rows="4"
              className={errors.notes ? 'error' : ''}
            />
            {errors.notes && <span className="error-message">{errors.notes}</span>}
          </div>

          {/* Submit Button */}
          <button type="submit" className="booking-submit-button">
            Request Booking
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
